// src/screens/ChargerStation.tsx
import React, { useEffect, useState } from 'react';
import {
  SafeAreaView,
  View,
  Image,
  StyleSheet,
  Platform,
  Text,
  ActivityIndicator,
  Pressable,
  KeyboardAvoidingView,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import SignInOptions from './SignInOptions';
import Header from '../components/Header';
import ChargerInfo from '../components/ChargingStation/ChargerInfo';
import ChargerDetails from '../components/ChargingStation/ChargerDetails';
import PaymentMethodWeb from '../components/ChargingStation/PaymentMethod.web';
import PaymentMethodIOS from '../components/ChargingStation/PaymentMethod.ios';
import PaymentMethodAndroid from '../components/ChargingStation/PaymentMethod.android';
import EmailSection from '../components/ChargingStation/EmailSection';
import CreateAccountSection from '../components/ChargingStation/CreateAccountSection';
import CheckoutSection from '../components/ChargingStation/CheckoutSection';
import MobilePaymentPlaceholder from '../components/ChargingStation/MobilePaymentPlaceholder';
import PricingBreakdown from '../components/ChargingStation/PricingBreakdown';
import PricingOverrideBadge from '../components/ChargingStation/PricingOverrideBadge';
import RedeemCodeInput from '../components/ChargingStation/RedeemCodeInput';
import Scheduler from './Scheduler';
import RoleService from '../services/role-service';
import PullToRefreshWrapper from '../components/common/PullToRefreshWrapper';
import { isCurrentlyUnavailable, getUnavailabilitySummary } from '../components/common/utils/unavailable-times-utils';

import { useChargingStation } from '../hooks/useChargingStation';
import type { RootStackParamList } from '../navigation/stackTypes';
import { notificationService } from '../services/notification-service';
import { getListOfFiles, downloadFilesFromServer } from '../components/common/utils/file-handler';
import { AttachmentObj } from '../models/file-handler-model';
import { UserSessionAttributes } from '../models/user-session-model';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  getChargerOfflineStatusSubtitle,
  isChargerOffline,
  isCsmsLiveSnapshotUnavailable,
} from '../models/charger-model';


const height =
  typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 0;

type ChargerStationNavigationProps = NativeStackScreenProps<
  RootStackParamList,
  'ChargerStation'
>;

const ChargerStation: React.FC<ChargerStationNavigationProps> = ({
  navigation,
  route,
}) => {
  // Pull in everything from our hook:
  const {
    // State
    email,
    guestEmail,
    storedEmail,
    selectedOption,
    showCreateAccount,
    myDistanceFromCharger,
    myTimeFromCharger,
    paymentData,
    paymentsDropdownOpen,
    order,
    isValidEmail,
    loadError,
    chargerInUse,
    chargerUnavailableMessage,
    scheduleStartTime,
    scheduleEndTime,
    selectedMaxKwh,
    scheduleSet,
    showFeeTooltipVisible,
    activeCharges,
    session,
    selectedCharger,
    stripeElements,
    setNewOrder,
    // Stripe handlers (web)
    onDropdownPressed,
    onPaymentSubmit,
    handleRemovePayment,

    // Stripe handlers (mobile)
    openPaymentSheet,

    // Checkout
    checkoutButtonDisabled,
    handleCheckoutButton,

    // Schedule
    setScheduleTimes,
    clearSchedule,

    // Tooltip
    showFeeTooltip,
    hideFeeTooltip,

    // UI handlers
    handleEmailChange,
    handleOptionSelect,
    handleCreateAccount,
    handleSignIn,

    // Refs
    scrollViewRef,
    bottomSheetModalRef,

    // Free override
    isFreeOverride,

    // Refresh
    refreshChargerData,
  } = useChargingStation(navigation, route);
  

  // State for charger background image
  const [chargerBackgroundUri, setChargerBackgroundUri] = useState<string | null>(null);
  const [loadingBackground, setLoadingBackground] = useState<boolean>(false);
  const [imageResizeMode, setImageResizeMode] = useState<'cover' | 'contain'>('cover');
  const userSession: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );

  // Load charger background image
  useEffect(() => {
    const loadChargerBackground = async () => {
      if (!selectedCharger?.uuid) {
        return;
      }

      setLoadingBackground(true);
      
      try {
        // Get list of files associated with this charger
        const files = await getListOfFiles(selectedCharger.uuid);
        
        if (!files || files.length === 0) {
          // console.log('No files found for charger:', selectedCharger.uuid);
          setLoadingBackground(false);
          return;
        }

        // Find the first image file (prioritize by identifier or just take first image)
        const imageFile = files.find((file: AttachmentObj) => {
          // Check if it's an image by MIME type
          const isImage = file.attachment?.type?.startsWith('image/') || 
                         file.documentname?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
          
          // Optionally, prioritize images with specific identifier
          // e.g., return isImage && file.identifier === 'charger-background';
          return isImage;
        });

        if (!imageFile) {
          console.log('No image files found for charger');
          setLoadingBackground(false);
          return;
        }

        console.log('Found charger image:', imageFile.documentname);

        if (Platform.OS === 'web') {
          // For web, we need to manually download and create a blob URL
          // Import the web-specific download function
          const { downloadFileBinaryWeb } = require('../services/file-handler-service');
          const JSZip = require('jszip');
          
          const body = {
            action: 100, // DOWNLOAD_FILE action
            docid: imageFile.documentid,
            documentname: imageFile.documentname,
            version: 'thumbnail', // Request thumbnail for faster loading
          };
          
          const response = await downloadFileBinaryWeb('filehandler/download', body);
          
          if (response.error) {
            console.error('Failed to download image:', response.error);
            return;
          }
          
          // Decompress the ZIP file
          const zip = new JSZip();
          const loadedZip = await zip.loadAsync(response.blob);
          const firstFile = Object.keys(loadedZip.files)[0];
          const unzippedBlob = await loadedZip.files[firstFile].async('blob');
          
          // Create blob URL for display
          const blobUrl = URL.createObjectURL(unzippedBlob);
          setChargerBackgroundUri(blobUrl);
          console.log('Charger background loaded (web):', blobUrl);
        } else {
          // On native, download to local file system with thumbnail version
          const downloadedUri = await downloadFilesFromServer(imageFile, undefined, 'thumbnail');
          
          if (downloadedUri) {
            setChargerBackgroundUri(downloadedUri as string);
            console.log('Charger background loaded (native):', downloadedUri);
          }
        }
      } catch (error) {
        console.error('Error loading charger background:', error);
      } finally {
        setLoadingBackground(false);
      }
    };

    loadChargerBackground();
    
    // Cleanup: revoke blob URLs on unmount (web only)
    return () => {
      if (Platform.OS === 'web' && chargerBackgroundUri && chargerBackgroundUri.startsWith('blob:')) {
        URL.revokeObjectURL(chargerBackgroundUri);
      }
    };
  }, [selectedCharger?.uuid]);

  const renderCharger = selectedCharger;

  // If no charger is selected (e.g., after logout), show loading or empty state
  if (!renderCharger || !renderCharger.uuid) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View
          style={[
            styles.containerBgColor,
            {
              height: height - 65,
              paddingBottom: Platform.OS === 'android' ? 0 : 10,
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
            },
          ]}
        >
          {Platform.OS !== 'web' && (
            <Header 
              headerText="Charging Station" 
            />
          )}
          <ActivityIndicator size="large" color="#51B868" />
          <Text style={{ color: '#8D8E90', marginTop: 16 }}>
            Loading charger details...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Toggle image resize mode
  const toggleImageResizeMode = () => {
    setImageResizeMode(prev => prev === 'cover' ? 'contain' : 'cover');
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View
        style={[
          styles.containerBgColor,
          {
            height: height - 65,
            paddingBottom: Platform.OS === 'android' ? 0 : 10,
            flex: 1,
          },
        ]}
      >
        {Platform.OS !== 'web' && (
          <Header 
            headerText="Charging Station" 
          />
        )}

        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 10 : 0}
        >
        <PullToRefreshWrapper
          pullText="Pull down to refresh charger status"
          refreshingText="Refreshing charger status..."
          onRefresh={refreshChargerData}
          keyboardShouldPersistTaps="handled"
        >
          {/* Charger Background Image */}
          {loadingBackground ? (
            <View
              style={[
                Platform.OS === 'web'
                  ? { aspectRatio: 1, width: '100%', height: 230 }
                  : { width: '100%', height: 300, marginBottom: 16 },
                { 
                  backgroundColor: '#1E1E1E', 
                  justifyContent: 'center', 
                  alignItems: 'center' 
                }
              ]}
            >
              <ActivityIndicator size="large" color="#51B868" />
            </View>
          ) : (
            <View style={styles.imageContainer}>
              <Image
                source={
                  chargerBackgroundUri
                    ? { uri: chargerBackgroundUri }
                    : require('../../assets/ChargerStationBg.png')
                }
                style={
                  Platform.OS === 'web'
                    ? { aspectRatio: 1, width: '100%', height: 230 }
                    : { width: '100%', height: 300, marginBottom: 16 }
                }
                resizeMode={imageResizeMode}
                alt="Charger Background Image"
              />
              
              {/* Resize Mode Toggle Button - Only show if custom image is loaded */}
              {chargerBackgroundUri && (
                <Pressable
                  style={styles.resizeButton}
                  onPress={toggleImageResizeMode}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <View style={styles.resizeButtonShadow}>
                    <View style={styles.resizeButtonInner}>
                      <MaterialIcons 
                        name={imageResizeMode === 'cover' ? 'zoom-out-map' : 'crop-free'} 
                        size={20} 
                        color="white" 
                      />
                    </View>
                  </View>
                </Pressable>
              )}
            </View>
          )}

          {/* ChargerInfo */}
          <ChargerInfo
            renderCharger={renderCharger}
            myDistanceFromCharger={myDistanceFromCharger}
            myTimeFromCharger={myTimeFromCharger}
            activeCharges={activeCharges}
          />

          <View style={styles.line} />

          {/* ChargerDetails */}
          <ChargerDetails renderCharger={renderCharger} />

          {/* Pricing Breakdown - Show detailed pricing information 
              Uncomment the following line to show a pricing breakdown
              We are currently not using this until more users request it
              I originally developed it based on apple asking for more pricing details
          */}
          {/* {!renderCharger.isOccupied && (
            <PricingBreakdown renderCharger={renderCharger} />
          )} */}

          {/* Show "Charger Offline", "Charger In Use" message, or payment components */}
          {isChargerOffline(renderCharger) ? null : chargerInUse ? (
            <View style={{ marginHorizontal: 16, marginVertical: 24, padding: 20, backgroundColor: '#292D33', borderRadius: 12 }}>
              <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#F97777', textAlign: 'center', marginBottom: 12 }}>
                Charger Unavailable
              </Text>
              <Text style={{ fontSize: 14, color: '#8D8E90', textAlign: 'center', lineHeight: 20 }}>
                {chargerUnavailableMessage || 'This charger is currently in use. Please come back later or select a different charger.'}
              </Text>
            </View>
          ) : (
            <>
              {/* PaymentMethod (platform-specific) */}
              {Platform.OS === 'web' && (
                <PaymentMethodWeb
                  renderChargerIsFree={renderCharger.isOccupied !== 0 && renderCharger.is_enabled === true}
                  activeCharges={activeCharges}
                  order={order}
                  paymentData={paymentData}
                  paymentsDropdownOpen={paymentsDropdownOpen}
                  onDropdownPressed={onDropdownPressed}
                  handleRemovePayment={handleRemovePayment}
                  onPaymentSubmit={onPaymentSubmit}
                  stripeElements={stripeElements}
                  loadError={loadError}
                />
              )}
              {Platform.OS === 'ios' && <PaymentMethodIOS />}
              {Platform.OS === 'android' && <PaymentMethodAndroid />}

              {/* EmailSection + CreateAccount + SignIn */}
              <EmailSection
                selectedChargerIsFree={renderCharger.isOccupied !== 0}
                activeCharges={activeCharges}
                order={order}
                sessionRole={session?.role}
                storedEmail={storedEmail}
                guestEmail={guestEmail}
                emailValid={isValidEmail}
                handleEmailChange={handleEmailChange}
                selectedOption={selectedOption}
                handleOptionSelect={handleOptionSelect}
                handleCreateAccount={handleCreateAccount}
                showCreateAccount={showCreateAccount}
                handleSignIn={handleSignIn}
                setNewOrder={setNewOrder}
                orderId={order?.id}
              />

              {/* MobilePaymentPlaceholder (logic lives in hook) */}
              {Platform.OS !== 'web' && <MobilePaymentPlaceholder />}

              {/* Scheduler - only show when no active charges */}
              {!renderCharger.isOccupied &&
                renderCharger.is_enabled === true &&
                order &&
                !activeCharges &&
                renderCharger.can_schedule && 
                userSession && 
                !RoleService.hasRole(userSession, 'guest') && (
                  <View style={{ marginHorizontal: 16 }}>
                    <Text className="text-base font-normal text-center text-slate-100">
                      Do you want to schedule this charge for later?
                    </Text>
                    <Scheduler
                      selectedCharger={renderCharger}
                      scheduleDate={scheduleStartTime}
                      setScheduleTimes={setScheduleTimes}
                      clearSchedule={clearSchedule}
                    />
                  </View>
                )}

              {/* Separator - only when scheduler is shown */}
              {!renderCharger.isOccupied &&
                renderCharger.is_enabled === true &&
                order &&
                !activeCharges && (
                  <View style={styles.lin2} className="py-1" />
                )}
            </>
          )}
          
          {/* Pricing Override Badge - shown when user has a discount */}
          {renderCharger?.user_pricing_override?.has_discount && (
            <PricingOverrideBadge override={renderCharger.user_pricing_override} />
          )}

          {/* Redeem Code Input - shown when charger has per-charger codes */}
          {renderCharger?.has_redeemable_codes && renderCharger?.uuid && (
            <RedeemCodeInput
              chargerId={renderCharger.uuid}
              onCodeRedeemed={() => {
                if (refreshChargerData) refreshChargerData();
              }}
            />
          )}

          {/* CheckoutSection - renders independently to show active charge advisory OR checkout button */}
          {/* If charger is offline, show offline banner */}
          {isChargerOffline(renderCharger) ? (
            <View style={styles.faultedContainer}>
              <View style={[styles.faultedIconContainer, { backgroundColor: 'rgba(107, 114, 128, 0.15)' }]}>
                <MaterialIcons name="cloud-off" size={48} color="#6B7280" />
              </View>
              <Text style={styles.faultedTitle}>Charger Offline</Text>
              <Text style={styles.faultedDescription}>
                {isCsmsLiveSnapshotUnavailable(renderCharger)
                  ? 'There is no trustworthy live snapshot from the charging network right now, so connector and vehicle status cannot be shown.'
                  : 'This charger is currently offline and unavailable for use.'}
                {'\n\n'}
                {getChargerOfflineStatusSubtitle(renderCharger)}
                {'\n\n'}
                Pull down to refresh and check if it has come back online.
              </Text>
              <View style={styles.faultedTip}>
                <MaterialIcons name="refresh" size={20} color="#51B868" style={{ marginRight: 8 }} />
                <Text style={styles.faultedTipText}>Pull down to refresh charger status</Text>
              </View>
            </View>
          ) : (renderCharger.ocpp_connector_status === 'Faulted' || renderCharger.chargerState?.connector_status === 'Faulted') ? (
            <View style={styles.faultedContainer}>
              <View style={styles.faultedIconContainer}>
                <MaterialIcons name="warning" size={48} color="#3B82F6" />
              </View>
              <Text style={styles.faultedTitle}>Charger Temporarily Unavailable</Text>
              <Text style={styles.faultedDescription}>
                This charger is currently experiencing an issue. If the charger has reset or recovered, pull down on this page to refresh and check if it's available to use.
              </Text>
              <View style={styles.faultedTip}>
                <MaterialIcons name="refresh" size={20} color="#51B868" style={{ marginRight: 8 }} />
                <Text style={styles.faultedTipText}>Pull down to refresh charger status</Text>
              </View>
            </View>
          ) : isCurrentlyUnavailable(renderCharger.unavailable_times ?? null) ? (
            <View style={styles.unavailableContainer}>
              <View style={styles.unavailableIconContainer}>
                <MaterialIcons name="schedule" size={40} color="#F59E0B" />
              </View>
              <Text style={styles.unavailableTitle}>Charger Currently Unavailable</Text>
              <Text style={styles.unavailableDescription}>
                {getUnavailabilitySummary(renderCharger.unavailable_times ?? null) || 'This charger is in a scheduled unavailable period.'}
                {'\n'}Pull down to refresh and check when it becomes available.
              </Text>
              <View style={styles.faultedTip}>
                <MaterialIcons name="refresh" size={20} color="#51B868" style={{ marginRight: 8 }} />
                <Text style={styles.faultedTipText}>Pull down to refresh charger status</Text>
              </View>
            </View>
          ) : (
            <CheckoutSection
              selectedChargerIsFree={renderCharger.isOccupied !== 0 && renderCharger.is_enabled === true}
              activeCharges={activeCharges}
              order={order}
              sessionRole={session?.role}
              scheduleSet={scheduleSet}
              paymentData={paymentData}
              isValidEmail={isValidEmail}
              handleCheckoutButton={handleCheckoutButton}
              handleOpenPaymentSheet={openPaymentSheet}
              showFeeTooltipVisible={showFeeTooltipVisible}
              showFeeTooltip={showFeeTooltip}
              hideFeeTooltip={hideFeeTooltip}
              isFreeOverride={isFreeOverride}
              billingType={renderCharger.billing_type}
            />
          )}
          
        </PullToRefreshWrapper>
        </KeyboardAvoidingView>

        <SignInOptions
          navigation={navigation}
          bottomSheetModalRef={bottomSheetModalRef as any}
          destination={'ChargerStation'}
          orderId={order?.id}
          // setNewOrder={(newOrder) => {
          //   console.log("setNewOrder", newOrder);
          //   // We need to wire "setNewOrder" so that if SignInOptions triggers
          //   // a brand-new order, we pass it into whichever stripe hook to reinitialize.
          //   // For simplicity here, we do nothing. In real code, you'd call
          //   // the onNewOrderCreated callback on the appropriate hook.
          // }}
          setNewOrder={setNewOrder}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  containerBgColor: {
    backgroundColor: '#0B1E1D',
  },
  line: Platform.select({
    web: {
      borderBottomColor: '#438b88',
      borderBottomWidth: 1,
      borderStyle: 'dashed',
      marginVertical: 10,
    },
    default: {
      borderBottomColor: '#438b88',
      borderBottomWidth: 1,
      marginVertical: 10,
    },
  }),
  lin2: {
    borderBottomColor: '#438b88',
    borderBottomWidth: 1,
    marginVertical: 10,
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
  },
  resizeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 10,
  },
  resizeButtonShadow: {
    backgroundColor: '#000000',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  resizeButtonInner: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 8,
    padding: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  faultedContainer: {
    marginHorizontal: 16,
    marginVertical: 24,
    padding: 24,
    backgroundColor: '#292D33',
    borderRadius: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  faultedIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  faultedTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 12,
  },
  faultedDescription: {
    fontSize: 14,
    color: '#8D8E90',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
  faultedTip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(81, 184, 104, 0.1)',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  faultedTipText: {
    fontSize: 14,
    color: '#51B868',
    fontWeight: '600',
  },
  unavailableContainer: {
    marginHorizontal: 16,
    marginVertical: 24,
    padding: 24,
    backgroundColor: '#292D33',
    borderRadius: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#F59E0B',
  },
  unavailableIconContainer: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  unavailableTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 12,
  },
  unavailableDescription: {
    fontSize: 14,
    color: '#8D8E90',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
});

export default ChargerStation;
